/**
 * Centralized API service for all backend API calls
 */

const API_BASE_URL = 'http://localhost:3000/api';

/**
 * Fetch all people from the backend
 */
export async function getPeople(): Promise<string[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/csv/people`);
    const data = await response.json();
    return data.people || [];
  } catch (error) {
    console.error('Error loading people:', error);
    return [];
  }
}

/**
 * Detect recurring transaction patterns
 */
export async function detectRecurring(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/analytics/detect-recurring`);
  if (!response.ok) {
    throw new Error('Failed to detect recurring transactions');
  }
  return await response.json();
}

/**
 * Create a new rule
 */
export async function createRule(ruleData: {
  pattern: string;
  category: string;
  person?: string | null;
  normalizedName?: string | null;
  markAsRecurring?: boolean;
  recurringType?: string | null;
  excludeFromAnalytics?: boolean;
}): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ruleData)
  });

  if (!response.ok) {
    const result = await response.json();
    throw new Error(result.error || 'Failed to create rule');
  }

  return await response.json();
}

/**
 * Create multiple rules from review decisions
 * PWA migration: Uses storage provider
 */
export async function createRulesFromReview(decisions: Array<{
  description: string;
  category: string;
  person?: string | null;
}>): Promise<any> {
  const { createRulesFromReview } = await import('./storage');
  return createRulesFromReview(decisions);
}

/**
 * Get all recurring transactions with status
 */
export async function getRecurringTransactions(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring`);
  if (!response.ok) {
    throw new Error('Failed to fetch recurring transactions');
  }
  return await response.json();
}

/**
 * Get upcoming recurring transactions (next 30 days)
 */
export async function getUpcomingRecurring(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/upcoming`);
  if (!response.ok) {
    throw new Error('Failed to fetch upcoming recurring transactions');
  }
  return await response.json();
}

/**
 * Archive a recurring transaction
 */
export async function archiveRecurring(pattern: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern })
  });

  if (!response.ok) {
    throw new Error('Failed to archive recurring transaction');
  }

  return await response.json();
}

/**
 * Unarchive a recurring transaction
 */
export async function unarchiveRecurring(pattern: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/unarchive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern })
  });

  if (!response.ok) {
    throw new Error('Failed to unarchive recurring transaction');
  }

  return await response.json();
}

/**
 * Get archived recurring transactions
 */
export async function getArchivedRecurring(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/archived`);
  if (!response.ok) {
    throw new Error('Failed to fetch archived recurring transactions');
  }
  return await response.json();
}

/**
 * Snooze a recurring transaction (suppress "possibly_ended" warning for X days)
 */
export async function snoozeRecurring(pattern: string, days: number = 90): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/snooze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern, days })
  });

  if (!response.ok) {
    throw new Error('Failed to snooze recurring transaction');
  }

  return await response.json();
}

/**
 * Get snoozed recurring transactions
 */
export async function getSnoozedRecurring(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/snoozed`);
  if (!response.ok) {
    throw new Error('Failed to fetch snoozed recurring transactions');
  }
  return await response.json();
}

/**
 * Dismiss a recurring pattern suggestion
 */
export async function dismissRecurringPattern(pattern: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/dismiss-pattern`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern })
  });

  if (!response.ok) {
    throw new Error('Failed to dismiss pattern');
  }

  return await response.json();
}

/**
 * Get dismissed recurring patterns
 */
export async function getDismissedPatterns(): Promise<{ dismissed: string[] }> {
  const response = await fetch(`${API_BASE_URL}/recurring/dismissed-patterns`);
  if (!response.ok) {
    throw new Error('Failed to fetch dismissed patterns');
  }
  return await response.json();
}

/**
 * Un-dismiss a recurring pattern (restore it)
 */
export async function undismissRecurringPattern(pattern: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/undismiss-pattern`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern })
  });

  if (!response.ok) {
    throw new Error('Failed to un-dismiss pattern');
  }

  return await response.json();
}

/**
 * Update recurring transaction interval
 */
export async function updateRecurringInterval(pattern: string, avgInterval: number): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/recurring/update-interval`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pattern, avgInterval })
  });

  if (!response.ok) {
    throw new Error('Failed to update recurring interval');
  }

  return await response.json();
}

/**
 * Suggest categorization rules based on transactions
 */
export async function suggestRules(transactionIds: string[]): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/analytics/suggest-rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transactionIds })
  });

  if (!response.ok) {
    throw new Error('Failed to suggest rules');
  }

  return await response.json();
}

/**
 * Get uncategorized transactions (optionally filtered by filename)
 */
export async function getUncategorizedTransactions(filename?: string): Promise<any> {
  const url = filename
    ? `${API_BASE_URL}/transactions/uncategorized?filename=${encodeURIComponent(filename)}`
    : `${API_BASE_URL}/transactions/uncategorized`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch uncategorized transactions');
  }

  return await response.json();
}

/**
 * Create multiple categorization rules at once
 */
export async function batchCreateRules(rules: Array<{
  pattern: string;
  category: string;
  person?: string | null;
  normalizedName?: string | null;
  markAsRecurring?: boolean;
  recurringType?: string | null;
  excludeFromAnalytics?: boolean;
}>): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/rules/batch-create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules })
  });

  if (!response.ok) {
    throw new Error('Failed to batch create rules');
  }

  return await response.json();
}

// ============================================================================
// LLM Configuration & Categorization
// ============================================================================

export type LLMProvider = 'ollama' | 'anthropic' | 'openai';

export type LLMConfigResponse = {
  provider: LLMProvider;
  apiKeyConfigured: boolean;
  maskedApiKey: string | null;
  model: string | null;
  enabled: boolean;
  optedIntoCloud: boolean;
  settings: {
    ollamaUrl: string;
    temperature: number;
    maxTokens: number;
  };
  usage: {
    totalRequests: number;
    totalTokens: number;
    lastUsed: string | null;
  };
};

export type LLMConfigUpdate = {
  provider?: LLMProvider;
  apiKey?: string | null;
  model?: string | null;
  enabled?: boolean;
  optedIntoCloud?: boolean;
  settings?: {
    ollamaUrl?: string;
    temperature?: number;
    maxTokens?: number;
  };
};

export type LLMCategorySuggestion = {
  transactionId: string;
  description: string;
  category: string;
  confidence: number;
  reasoning?: string;
  displayName?: string;
  method?: string;
  error?: string;
};

/**
 * Get LLM configuration (API key is masked for security)
 */
export async function getLLMConfig(): Promise<LLMConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/llm/config`);
  if (!response.ok) {
    throw new Error('Failed to fetch LLM configuration');
  }
  const data = await response.json();
  return data.config;
}

/**
 * Update LLM configuration
 */
export async function updateLLMConfig(config: LLMConfigUpdate): Promise<LLMConfigResponse> {
  const response = await fetch(`${API_BASE_URL}/llm/config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update LLM configuration');
  }

  const data = await response.json();
  return data.config;
}

/**
 * Test LLM connection with current configuration
 */
export async function testLLMConnection(): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  provider?: string;
  model?: string;
}> {
  const response = await fetch(`${API_BASE_URL}/llm/test-connection`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });

  const data = await response.json();
  return data;
}

/**
 * Categorize a single transaction using LLM
 */
export async function categorizeWithLLM(transactionId: string): Promise<LLMCategorySuggestion> {
  const response = await fetch(`${API_BASE_URL}/llm/categorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transactionId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to categorize transaction');
  }

  const data = await response.json();
  return {
    ...data.suggestion,
    transactionId: data.transactionId
  };
}

/**
 * Categorize multiple transactions using LLM (batch)
 */
export async function batchCategorizeWithLLM(transactionIds: string[]): Promise<LLMCategorySuggestion[]> {
  const response = await fetch(`${API_BASE_URL}/llm/categorize-batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transactionIds })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to batch categorize transactions');
  }

  const data = await response.json();
  return data.suggestions;
}

/**
 * Get LLM usage statistics
 */
export async function getLLMUsage(): Promise<{
  totalRequests: number;
  totalTokens: number;
  lastUsed: string | null;
}> {
  const response = await fetch(`${API_BASE_URL}/llm/usage`);
  if (!response.ok) {
    throw new Error('Failed to fetch LLM usage statistics');
  }
  const data = await response.json();
  return data.usage;
}
